import { GlobalProvider } from '@if/mui'
import { CssBaseline, ThemeOptions, ThemeProvider, createTheme } from '@mui/material'
import { blue } from '@mui/material/colors'
import type {} from '@mui/x-data-grid/themeAugmentation'
import ErrorFallback from '@src/components/ErrorFallback'
import UserProvider from '@src/contexts/UserProvider'
import { AxiosError } from 'axios'
import { forwardRef, useMemo, useState } from 'react'
import { BrowserRouter as Router, Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom'
import TopLevelRoutes from './Routes'

const LinkComponent = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props
    // Map href (MUI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} />
  },
)
LinkComponent.displayName = 'LinkBehavior'

const GLOBAL_OVERRIDES: ThemeOptions = {
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkComponent,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiListItemButton: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiIconButton: {
      defaultProps: {
        LinkComponent,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:first-of-type > .MuiGrid-root:first-of-type': { marginTop: 0 },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'text',
        LinkComponent,
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    },
    MuiDialogContent: {
      defaultProps: {
        dividers: true,
      },
    },
    MuiTab: {
      defaultProps: {
        LinkComponent,
      },
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
      },
    },
  },
}

const App = () => {
  const [error, setError] = useState<AxiosError | Error | null>(null)
  const theme = useMemo(
    () =>
      createTheme({
        ...GLOBAL_OVERRIDES,
        colorSchemes: {
          dark: {
            palette: {
              primary: { main: blue[200] },
            },
          },
          light: {
            palette: {
              primary: { main: blue[700] },
            },
          },
        },
      }),
    [],
  )
  return (
    <ThemeProvider theme={theme}>
      <GlobalProvider>
        <UserProvider>
          {error && <ErrorFallback error={error} resetError={setError} />}
          <CssBaseline />
          <Router>
            <TopLevelRoutes />
          </Router>
        </UserProvider>
      </GlobalProvider>
    </ThemeProvider>
  )
}

export default App
